import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

export type AlgoliaSearchKey = {
  appId: string;
  searchKey: string;
  userToken: string;
  expiresAt: number;
};

const AlgoliaSearchKeyContext = createContext<AlgoliaSearchKey | null>(null);

interface ProviderProps {
  children: ReactNode;
  initialKey?: AlgoliaSearchKey | null;
}

const REFRESH_LEAD_RATIO = 0.2;
const MIN_REFRESH_LEAD_MS = 5_000;

function getRefreshDelay(expiresAt: number): number {
  const nowMs = Date.now();
  const expiresAtMs = expiresAt * 1000;
  const lifetimeMs = Math.max(0, expiresAtMs - nowMs);
  const lead = Math.max(MIN_REFRESH_LEAD_MS, lifetimeMs * REFRESH_LEAD_RATIO);
  return Math.max(0, lifetimeMs - lead);
}

export const AlgoliaSearchKeyProvider = ({
  children,
  initialKey,
}: ProviderProps) => {
  const [value, setValue] = useState<AlgoliaSearchKey | null>(
    initialKey ?? null
  );
  const inFlightRef = useRef(false);

  const fetchKey = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      const res = await fetch('/api/algolia/key', {
        credentials: 'same-origin',
      });
      if (!res.ok)
        throw new Error(`Failed to fetch Algolia key: ${res.status}`);
      const data = await res.json();
      setValue({
        appId: data.appId,
        searchKey: data.searchKey,
        userToken: data.userToken,
        expiresAt: data.expiresAt,
      });
    } catch (err) {
      console.error('Failed to fetch Algolia search key', err);
    } finally {
      inFlightRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (!value) {
      fetchKey();
      return;
    }
    const delay = getRefreshDelay(value.expiresAt);
    const timer = setTimeout(fetchKey, delay);
    return () => clearTimeout(timer);
  }, [value, fetchKey]);

  return (
    <AlgoliaSearchKeyContext.Provider value={value}>
      {children}
    </AlgoliaSearchKeyContext.Provider>
  );
};

export const useAlgoliaSearchKey = (): AlgoliaSearchKey | null =>
  useContext(AlgoliaSearchKeyContext);
